/**
 * EnrolmentApprovalPanel — the therapist's approve/decline decision after the
 * intake call (AIMHI Direct AD-08, spec §4.7).
 *
 * This is the gate that opens AI support. Before it, the client sits in the
 * portal; after it, the full app unlocks and their programme window starts.
 * It is therefore written as a clinical act rather than a status toggle:
 *
 *  - Approving requires an explicit duty-of-care acknowledgement. The therapist
 *    is taking responsibility for a person they have just assessed and judging
 *    that an AI companion is appropriate for them.
 *  - Declining requires a clinical reason AND a referral, because a client
 *    found unsuitable for a light-touch pilot is precisely the person for whom
 *    a dead end would be most harmful (§8.5). The server rejects an incomplete
 *    decline, so this is not merely a UI courtesy.
 *
 * Renders nothing unless the client has an enrolment awaiting a decision, so it
 * is safe to mount on any therapist surface.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Stack, Alert, Chip, TextField,
  Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Divider
} from '@mui/material';
import { CheckCircle as ApproveIcon, Block as DeclineIcon } from '@mui/icons-material';
import api from '../../services/api';

export default function EnrolmentApprovalPanel({ clientId, liveCallId, token, onDecision }) {
  const [enrolment, setEnrolment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const [approveOpen, setApproveOpen] = useState(false);
  const [dutyAck, setDutyAck] = useState(false);
  const [rationale, setRationale] = useState('');

  const [declineOpen, setDeclineOpen] = useState(false);
  const [clinicalReason, setClinicalReason] = useState('');
  const [referral, setReferral] = useState('');
  const [toldDuringCall, setToldDuringCall] = useState(true);

  const load = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await api.get('/enrolments', token);
      const mine = (res.data?.enrolments || [])
        .filter(e => String(e.clientId?._id || e.clientId) === String(clientId));
      setEnrolment(mine[0] || null);
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not load the enrolment');
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  useEffect(() => { load(); }, [load]);

  const submitApprove = async () => {
    try {
      setBusy(true); setError('');
      await api.post(`/enrolments/${enrolment._id}/approve`, {
        dutyOfCareAcknowledged: dutyAck,
        clinicalRationale: rationale || undefined,
        liveCallId: liveCallId || undefined
      }, token);
      setApproveOpen(false);
      setNotice('Approved. The client now has access to their AI companion and their programme has started.');
      await load();
      if (onDecision) onDecision('approved');
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not record the approval');
    } finally { setBusy(false); }
  };

  const submitDecline = async () => {
    try {
      setBusy(true); setError('');
      await api.post(`/enrolments/${enrolment._id}/decline`, {
        declineRecord: {
          clinicalReason, referralRecommendation: referral, toldDuringCall,
          followUpRequired: !toldDuringCall
        },
        liveCallId: liveCallId || undefined
      }, token);
      setDeclineOpen(false);
      setNotice('Recorded. The client keeps their explanation, your referral and the crisis contacts in their portal.');
      await load();
      if (onDecision) onDecision('declined');
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not record the decision');
    } finally { setBusy(false); }
  };


  // What the client did not simply agree to at consent, and whether consent is
  // outstanding. Shown at both stages: before the call so it can be prepared
  // for, and at the decision so nobody approves blind to it.
  const renderConsentNotes = () => {
    const points = enrolment?.consentPointsToDiscuss || [];
    const consent = enrolment?.consent;
    if (!points.length && (!consent || consent.complete || !consent.configured)) return null;

    return (
      <Box sx={{ mb: 2 }}>
        {points.length > 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              The client raised {points.length === 1 ? 'a point' : `${points.length} points`} at consent
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {points.map(p => (
                <Box key={p.clauseId}>
                  <Chip
                    size="small"
                    color={p.response === 'understand_and_do_not_agree' ? 'error' : 'default'}
                    label={p.response === 'understand_and_do_not_agree' ? 'Does not agree' : 'Not sure what it means'}
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body2" component="span">{p.text}</Typography>
                </Box>
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Go through these in the appointment. The client confirms them in their own portal
              afterwards — you cannot record consent on their behalf.
            </Typography>
          </Alert>
        )}
        {consent && consent.configured && !consent.complete && (
          <Alert severity="warning">
            Consent is not complete — outstanding: <strong>{(consent.outstanding || []).join(', ')}</strong>.
            You will not be able to approve until the client has confirmed.
          </Alert>
        )}
      </Box>
    );
  };

  if (loading) return <CircularProgress size={20} />;
  if (!enrolment) return null;

  // The decision only becomes available once the call has happened, and
  // nothing else in the UI moves a booking to attended — without this the
  // journey stalls at awaiting_call with no way forward.
  if (enrolment.status === 'awaiting_call') {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>Intake call booked</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Once you have met this client, mark the call attended. You can then approve them for the
            programme or record that it is not the right fit.
          </Typography>
          {renderConsentNotes()}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Button
            variant="contained"
            disabled={busy}
            onClick={async () => {
              try {
                setBusy(true); setError('');
                await api.post(`/enrolments/${enrolment._id}/attended`, {}, token);
                await load();
              } catch (err) {
                setError(err.response?.data?.msg || 'Could not mark the call attended');
              } finally { setBusy(false); }
            }}
          >
            {busy ? 'Saving…' : 'Mark call attended'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (enrolment.status !== 'pending_approval') {
    return (
      <Alert severity={enrolment.status === 'active' ? 'success' : 'info'} sx={{ mb: 2 }}>
        Programme status: <strong>{enrolment.status.replace(/_/g, ' ')}</strong>
        {enrolment.status === 'active' && enrolment.endsAt &&
          ` — running until ${new Date(enrolment.endsAt).toLocaleDateString()}`}
      </Alert>
    );
  }

  return (
    <Card variant="outlined" sx={{ mb: 2, borderColor: 'primary.main' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Typography variant="h6">Decision needed</Typography>
          <Chip size="small" color="primary" label="Awaiting your approval" />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This client has completed their intake call. Their place is held indefinitely until you
          decide — there is no time limit on them. Approving starts their programme and opens their
          AI companion; declining releases the place and gives them your referral.
        </Typography>

        {renderConsentNotes()}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            variant="contained"
            startIcon={<ApproveIcon />}
            onClick={() => setApproveOpen(true)}
            disabled={!!(enrolment?.consent?.configured && !enrolment?.consent?.complete)}
          >
            Approve for the programme
          </Button>
          <Button variant="outlined" color="error" startIcon={<DeclineIcon />} onClick={() => setDeclineOpen(true)}>
            Not suitable
          </Button>
        </Stack>
      </CardContent>

      {/* Approve */}
      <Dialog open={approveOpen} onClose={() => !busy && setApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve this client for the programme</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* No persona choice: approval applies the one this programme's
                onboarding flow names (ST-20260915-0004, 15 Sep 2026). */}
            <Typography variant="body2" color="text.secondary">
              The client's AI companion is the one set for this programme. The client chooses its
              appearance and voice themselves.
            </Typography>
            <TextField
              fullWidth multiline minRows={3} label="Clinical rationale (optional)"
              value={rationale} onChange={(e) => setRationale(e.target.value)}
              helperText="Recorded with your approval. Useful to your supervisor and to you later."
            />
            <Divider />
            <FormControlLabel
              control={<Checkbox checked={dutyAck} onChange={(e) => setDutyAck(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  I have assessed this client, I accept clinical responsibility for their care during
                  this programme, and I judge AI-supported care between sessions to be appropriate
                  for them.
                </Typography>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={submitApprove} disabled={!dutyAck || busy}>
            {busy ? 'Recording…' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline */}
      <Dialog open={declineOpen} onClose={() => !busy && setDeclineOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>This client is not suitable for the programme</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            They have been assessed by an accredited therapist and found to need something else. Both
            fields below are required — the client sees your explanation and referral in their portal,
            alongside the crisis contacts.
          </Alert>
          <Stack spacing={2}>
            <TextField
              fullWidth multiline minRows={3} required label="Clinical reason"
              value={clinicalReason} onChange={(e) => setClinicalReason(e.target.value)}
            />
            <TextField
              fullWidth multiline minRows={3} required label="Referral or signposting recommendation"
              value={referral} onChange={(e) => setReferral(e.target.value)}
              helperText="Primary care, a specialist service, a university service, or crisis support where relevant."
            />
            <FormControlLabel
              control={<Checkbox checked={toldDuringCall} onChange={(e) => setToldDuringCall(e.target.checked)} />}
              label="I told the client during the call"
            />
            {!toldDuringCall && (
              <Alert severity="warning">
                This will be flagged as needing follow-up contact, so they do not learn of the decision
                only from a screen.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineOpen(false)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained" color="error" onClick={submitDecline}
            disabled={busy || !clinicalReason.trim() || !referral.trim()}
          >
            {busy ? 'Recording…' : 'Record decision'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
